import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactFileUploadWithNotes from 'shared/design-system/components/TrueImpactFileUploadWithNotes';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { useNewNpoAnswer } from 'shared/apollo/pageHooks/useNewNpoAnswer';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { StyledTrueImpactText } from './ImpactIndicator/IndicatorTextarea';
import IndicatorTextareaRender from './ImpactIndicator/IndicatorTextareaRender';
import { BottomBar } from './BottomBar';
import ShowGuidanceText from './Review/ReportCustomQuestionType/ShowGuidanceText';
import { resultFileUpload } from './IndicatorDetails/utils';
import { GuidanceTextResponse } from './utils';

export const MAX_NOTE_CHARS = 500;
const MAX_FILE_SIZE = 512 * 1000 * 1000; // 512mb
const acceptedFileString = 'DOC, DOCX, PDF, XLSX, CSV';
const allowedFileExtensions = [
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

interface NoteFile {
  fileName?: string;
  id?: number;
  url?: string;
}

export const parseNoteValue = (
  value: string | undefined,
  hasFileUpload: boolean,
): { description: string; files: NoteFile[] } => {
  if (!value) return { description: '', files: [] };
  if (!hasFileUpload) return { description: value, files: [] };
  try {
    const parsed = JSON.parse(value);
    return {
      description: parsed.description || '',
      files: Array.isArray(parsed.files) ? parsed.files : [],
    };
  } catch (_ex) {
    return { description: '', files: [] };
  }
};

const serializeNoteValue = (
  description: string,
  files: TrueImpactFile[],
  hasFileUpload: boolean,
): string => {
  if (!hasFileUpload) return description;
  return JSON.stringify({
    description,
    files: files.map(f => ({ fileName: f.fileName, id: f.id, url: f.url })),
  });
};

const StyledDiv = styled.div`
  text-align: center;
  .note-title {
    max-width: 574px;
    margin: 48px auto 0;
  }
  .note-body {
    max-width: 502px;
    margin: ${SPACERS.medium} auto ${SPACERS.large};
  }
  .note-upload-box {
    justify-content: center;
  }
`;

const NoteOnlyWrapper = styled(StyledTrueImpactText)`
  max-width: 540px;
  margin: 0 auto;
  textarea {
    width: 100%;
    resize: vertical;
    font-size: 16px;
    border: transparent;
    padding: ${SPACERS.small};
    background-color: ${COLORS.greyLightest};
  }
`;

interface SectionNoteUploadProps {
  reportId: number | string;
  questionId: number;
  title: string;
  body?: string;
  hasFileUpload?: boolean;
  hideBottomBar?: boolean;
  guidanceText?: GuidanceTextResponse;
  initialValue?: string;
  onNext: () => unknown;
  onPrevious: () => unknown;
  beforeNext?: (description: string) => Promise<boolean>;
}

const SectionNoteUpload: React.FC<SectionNoteUploadProps> = ({
  reportId,
  questionId,
  title,
  body,
  hasFileUpload = false,
  hideBottomBar,
  guidanceText,
  initialValue,
  onNext,
  onPrevious,
  beforeNext,
}) => {
  const { run } = useNewNpoAnswer();
  const parsed = parseNoteValue(initialValue, hasFileUpload);
  const [description, setDescription] = useState<string>(parsed.description);
  const [files, setFiles] = useState<TrueImpactFile[]>(parsed.files);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const next = parseNoteValue(initialValue, hasFileUpload);
    setDescription(next.description);
    setFiles(next.files);
  }, [initialValue, hasFileUpload]);

  const handleFileUpload = useCallback(
    async (file: File | undefined) => {
      if (!file || files.length >= 10) return;
      if (files.some(f => f.fileName === file.name)) {
        setUploadError(`"${file.name}" has already been uploaded.`);
        return;
      }
      setUploadError(undefined);
      setIsUploading(true);
      const uploadResult = await resultFileUpload(reportId, file);
      setIsUploading(false);
      if (!uploadResult.success) {
        setUploadError(
          'Sorry, we could not upload that file. Please try again.',
        );
        return;
      }
      setFiles(prev => [
        ...prev,
        {
          id: uploadResult.id,
          url: uploadResult.url,
          fileName: file.name,
          description,
        },
      ]);
    },
    [reportId, files, description],
  );

  const handleDelete = useCallback((fileName: string) => {
    setFiles(prev => prev.filter(f => f.fileName !== fileName));
  }, []);

  const handleNext = useCallback(async () => {
    if (beforeNext) {
      // this checks level1 feedback for donor name
      const proceed = await beforeNext(description);
      if (!proceed) return;
    }
    run(
      {
        reportId: Number(reportId),
        questionId,
        text: serializeNoteValue(description, files, hasFileUpload),
        questionText: title,
      },
      () => onNext(),
    );
  }, [
    run,
    reportId,
    questionId,
    description,
    files,
    hasFileUpload,
    title,
    onNext,
    beforeNext,
  ]);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-page" className="note-title">
        {title}
      </TrueImpactText>
      <ShowGuidanceText questionId={questionId} guidanceText={guidanceText} />
      {body && <TrueImpactText className="note-body">{body}</TrueImpactText>}
      {hasFileUpload ? (
        <TrueImpactFileUploadWithNotes
          files={files}
          setFiles={setFiles}
          acceptedFileString={acceptedFileString}
          maxFileSize={MAX_FILE_SIZE}
          allowedFileExtensions={allowedFileExtensions}
          placeholder="Add any notes here..."
          handleFileUpload={handleFileUpload}
          handleDelete={handleDelete}
          description={description}
          setDescription={setDescription}
          className="note-upload-box"
          questionId={questionId}
          isUploading={isUploading}
          errorMessage={uploadError}
          maxChars={MAX_NOTE_CHARS}
        />
      ) : (
        <NoteOnlyWrapper variant="body" textareaBackgroundColor="whiteBase">
          <IndicatorTextareaRender
            id={`note-${questionId}`}
            questionId={questionId}
            handleSave={setDescription}
            placeholder="Add any notes here..."
            initialValue={description}
            maxLength={MAX_NOTE_CHARS}
          />
        </NoteOnlyWrapper>
      )}
      {!hideBottomBar && (
        <BottomBar
          disabled={false}
          hideBack={false}
          hideNext={false}
          handleBack={onPrevious}
          handleNext={handleNext}
          backTitle="Back"
          nextTitle="Save & Continue"
        />
      )}
    </StyledDiv>
  );
};

export default SectionNoteUpload;
